import styles from './Marquee.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Runner } from './Runner';
import { useMedia } from 'react-use';

const Marquee = ({
  className = '',
  pauseOnHover = false,
  pauseOnClick = false,
  direction = 'left',
  speed = 20,
  delay = 0,
  cyclesNumber = 0,
  iterationTimingFunction = 'linear',
  triggerDistance = 10,
  onClick = () => {},
  textColor,
  backgroundColor,
  isMobileVisible,
  paddings = 20,
  children = null,
  isSSR,
  promoText,
  ...rest
}) => {
  const isMobile = useMedia('(max-width: 810px)', false);
  const containerRef = useRef(null);
  const marqueeRef = useRef(null);
  const [playAnimation, setPlayAnimation] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!containerRef.current && !marqueeRef.current) return;

    setTimeout(() => {
      const containerWidth = containerRef?.current?.getBoundingClientRect()?.width;
      const marqueeWidth = marqueeRef?.current?.getBoundingClientRect()?.width;

      setPlayAnimation(containerWidth <= marqueeWidth + triggerDistance * 2 && isMobile);
    }, 10);
  }, [isMobile, containerRef, marqueeRef, promoText]);

  if (!isMounted) {
    return null;
  }

  if (isMobile && !isMobileVisible) return null;

  return (
    <div
      ref={containerRef}
      className={clsx(styles.container, className)}
      style={{
        '--text-color': textColor || 'white',
        '--background-color': backgroundColor || 'black',
      }}
      onClick={() => onClick()}
    >
      {isMobile && playAnimation ? (
        <Runner
          play={isMobile && playAnimation}
          direction={direction}
          speed={speed}
          delay={delay}
          loop={cyclesNumber}
          gradient={false}
          pauseOnHover
          pauseOnClick
          isSSR={isSSR}
          style={{
            '--iteration-timing-function': iterationTimingFunction,
            overflowY: 'hidden',
          }}
        >
          <div
            ref={marqueeRef}
            className={clsx(styles.marquee, isMobile && styles.marqueeMobile)}
            style={{
              '--paddings': `${paddings}px`,
            }}
          >
            {children}
          </div>
        </Runner>
      ) : (
        <div
          ref={marqueeRef}
          className={clsx(styles.marquee, isMobile && styles.marqueeMobile)}
          style={{
            '--paddings': `${paddings}px`,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

Marquee.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  speed: PropTypes.number,
  delay: PropTypes.number,
  cyclesNumber: PropTypes.number,
  triggerDistance: PropTypes.number,
  paddings: PropTypes.number,
  pauseOnHover: PropTypes.bool,
  pauseOnClick: PropTypes.bool,
  direction: PropTypes.oneOf(['left', 'right']),
  iterationTimingFunction: PropTypes.oneOf(['linear', 'ease-in', 'ease-out', 'ease-in-out']),
  onClick: PropTypes.func,
};

export default Marquee;
